import React, { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import SettingsBreadcrumb from '../../../../components/SettingsBreadcrumb';

type Publicacao = {
  id: number;
  titulo: string;
  alcance: number;
  curtidas: number;
  comentarios: number;
  compartilhamentos: number;
  rede: string;
};

type DadosGrafico = {
  nome: string;
  alcance: number;
  curtidas: number;
  comentarios: number;
  compartilhamentos: number;
};

const publicacoes: Publicacao[] = [
  {
    id: 1,
    titulo: 'Postagem 1',
    alcance: 5000,
    curtidas: 300,
    comentarios: 50,
    compartilhamentos: 20,
    rede: 'LinkedIn',
  },
  {
    id: 2,
    titulo: 'Postagem 2',
    alcance: 7000,
    curtidas: 450,
    comentarios: 70,
    compartilhamentos: 25,
    rede: 'LinkedIn',
  },
  {
    id: 3,
    titulo: 'Postagem 3',
    alcance: 6000,
    curtidas: 350,
    comentarios: 60,
    compartilhamentos: 30,
    rede: 'Twitter',
  },
  {
    id: 4,
    titulo: 'Postagem 4',
    alcance: 8000,
    curtidas: 500,
    comentarios: 80,
    compartilhamentos: 40,
    rede: 'Facebook',
  },
];

const getDadosAgregados = (dados: Publicacao[]): DadosGrafico =>
  dados.reduce(
    (acc, cur) => ({
      nome: 'Todos os Posts',
      alcance: acc.alcance + cur.alcance,
      curtidas: acc.curtidas + cur.curtidas,
      comentarios: acc.comentarios + cur.comentarios,
      compartilhamentos: acc.compartilhamentos + cur.compartilhamentos,
    }),
    {
      nome: 'Todos os Posts',
      alcance: 0,
      curtidas: 0,
      comentarios: 0,
      compartilhamentos: 0,
    }
  );

const exportarParaCSV = (dados: DadosGrafico[], rede: string) => {
  let csv = 'Publicação,Alcance,Curtidas,Comentários,Compartilhamentos\n';
  dados.forEach((linha) => {
    csv += `${linha.nome},${linha.alcance},${linha.curtidas},${linha.comentarios},${linha.compartilhamentos}\n`;
  });
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  const nomeArquivo =
    rede === 'todas'
      ? 'relatorio_cms_todas.csv'
      : `relatorio_cms_${rede.toLowerCase()}.csv`;
  link.setAttribute('href', url);
  link.setAttribute('download', nomeArquivo);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};


const RelatorioPublicacoesLinkedin = () => {
  const [publicacaoSelecionada, setPublicacaoSelecionada] = useState<number | 'todos'>('todos');
  const [redeSelecionada, setRedeSelecionada] = useState<string>('todas');

  const publicacoesFiltradas =
    redeSelecionada === 'todas'
      ? publicacoes
      : publicacoes.filter((pub) => pub.rede === redeSelecionada);

  let dadosGrafico: DadosGrafico[] = [];
  if (publicacaoSelecionada === 'todos') {
    dadosGrafico = [getDadosAgregados(publicacoesFiltradas)];
  } else {
    const publicacao = publicacoesFiltradas.find(
      (pub) => pub.id === publicacaoSelecionada
    );
    if (publicacao) {
      dadosGrafico = [
        {
          nome: publicacao.titulo,
          alcance: publicacao.alcance,
          curtidas: publicacao.curtidas,
          comentarios: publicacao.comentarios,
          compartilhamentos: publicacao.compartilhamentos,
        },
      ];
    }
  }

  return (
    <Flex
      direction="column"
      p={6}
      bg="gray.50"
      w="100%"
      minH="100vh"
      overflow="auto"
    >
      <SettingsBreadcrumb />
      <Heading mt="30px" fontWeight="600" fontSize="21px" color="#6F6F6F" w="100%" mb={6}>
        Relatório de Desempenho das Publicações
      </Heading>

      <Flex justify="center" mb={4} gap={4} flexWrap="wrap">
        <Select
          value={redeSelecionada}
          onChange={(e) => {
            setRedeSelecionada(e.target.value);
            setPublicacaoSelecionada('todos');
          }}
          w="300px"
          variant="filled"
        >
          <option value="todas">Todas as Redes</option>
          <option value="LinkedIn">LinkedIn</option>
          <option value="Twitter">Twitter</option>
          <option value="Facebook">Facebook</option>
          <option value="Instagram">Instagram</option>
        </Select>

        <Select
          value={publicacaoSelecionada === 'todos' ? 'todos' : publicacaoSelecionada}
          onChange={(e) => {
            const value = e.target.value;
            setPublicacaoSelecionada(value === 'todos' ? 'todos' : parseInt(value));
          }}
          w="300px"
          variant="filled"
        >
          <option value="" disabled>
            Selecione a publicação
          </option>
          <option value="todos">Todos os Posts (Agregado)</option>
          {publicacoesFiltradas.map((pub) => (
            <option key={pub.id} value={pub.id}>
              {pub.titulo} - {pub.rede}
            </option>
          ))}
        </Select>

      </Flex>

      <Box
        w="100%"
        height="60vh"
        mb={6}
        bg="white"
        p={4}
        borderRadius="md"
        boxShadow="md"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={dadosGrafico}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="nome" />
            <YAxis />
            <Tooltip />
            <Legend verticalAlign="top" height={36} />
            <Bar dataKey="alcance" fill="#82ca9d" name="Alcance" />
            <Bar dataKey="curtidas" fill="#8884d8" name="Curtidas" />
            <Bar dataKey="comentarios" fill="#ffc658" name="Comentários" />
            <Bar dataKey="compartilhamentos" fill="#ff8042" name="Compartilhamentos" />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      <Stack align="center">
        <Button colorScheme="blue" onClick={() => exportarParaCSV(dadosGrafico, redeSelecionada)}>
          Exportar Dados
        </Button>
        <Text mt={4} fontSize="lg">
          Clique para exportar os dados e realizar uma análise detalhada.
        </Text>
      </Stack>
    </Flex>
  );
};

export default RelatorioPublicacoesLinkedin;
